/**
 * API configuration for the events platform
 * Centralizes all API URL management
 */

// Determine if we're using Supabase or local backend
// Default to Supabase in production to avoid localhost fallbacks
const isProd = process.env.NODE_ENV === 'production';
const useSupabase =
  Boolean(process.env.REACT_APP_SUPABASE_URL) &&
  (isProd || process.env.REACT_APP_USE_SUPABASE === 'true' || !process.env.REACT_APP_API_URL);

// API Base URLs
export const API_BASE_URL = useSupabase
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
  : // In production, never fall back to localhost
    isProd
    ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
    : process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

// Remove /api suffix for non-API endpoints (like Stripe)
export const BACKEND_URL = useSupabase
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
  : isProd
    ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
    : process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3002';

// Helper to build API endpoints
export const buildApiUrl = (endpoint) => {
  // Remove leading slash if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

  // Certain endpoints are only served by the ingestion API, not Supabase Edge Functions
  const forceLocalApi = [
    'admin/events/video', // start video generation (Veo/Hailuo/Sora)
    'admin/video-status', // check video generation status
  ];
  const isForceLocal = forceLocalApi.some(
    (p) => cleanEndpoint === p || cleanEndpoint.startsWith(`${p}/`)
  );
  if (isForceLocal) {
    // Prefer explicit local API URL when available
    if (!isProd && process.env.REACT_APP_API_URL) {
      const localApi = process.env.REACT_APP_API_URL;
      const separator = localApi.endsWith('/') ? '' : '/';
      return `${localApi}${separator}${cleanEndpoint}`;
    }
    // In production or if no explicit local API is set, still attempt BACKEND_URL
    const backend = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
    return `${backend}/${cleanEndpoint}`;
  }

  // Endpoints that exist in Supabase Edge Functions
  const supabaseEndpoints = [
    'rsvp/',
    'host/',
    'auth/',
    'admin/',
    'events/',
    'discovery/',
    'user-data/',
    'onboarding/',
    'stripe/',
    'analytics/',
    'host-tools/',
    'budget-tools/',
    'promotion-enhanced/',
    'assistant-planner/',
    'social-content-generator',
    'social-scheduler/',
    'weather-service',
    'users/me/rsvps',
    'users/saved-events',
    'users/preferences',
  ];

  // Check if we should use Supabase for this endpoint
  const shouldUseSupabase =
    process.env.REACT_APP_SUPABASE_URL &&
    supabaseEndpoints.some((p) => cleanEndpoint.startsWith(p));

  // Use Supabase if the endpoint exists there
  if (shouldUseSupabase) {
    // Route to Supabase Edge Functions
    const supabaseUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`;

    // Apply endpoint mappings for Supabase
    if (cleanEndpoint === 'users/me/rsvps' || cleanEndpoint.startsWith('users/me/rsvps?')) {
      return `${supabaseUrl}/rsvp/user${cleanEndpoint.includes('?') ? cleanEndpoint.substring(cleanEndpoint.indexOf('?')) : ''}`;
    }

    return `${supabaseUrl}/${cleanEndpoint}`;
  }

  // Otherwise use local API if configured
  if (!isProd && process.env.REACT_APP_API_URL) {
    const localApi = process.env.REACT_APP_API_URL;
    const separator = localApi.endsWith('/') ? '' : '/';
    return `${localApi}${separator}${cleanEndpoint}`;
  }

  // Default fallback to Supabase if no local API
  return `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${cleanEndpoint}`;
};

// Old code kept for reference but not used
const _legacySupabaseMapping = (cleanEndpoint) => {
  // For Supabase, we need to adjust some endpoint paths
  if (useSupabase) {
    // Map old endpoints to new Edge Function structure
    const endpointMappings = {
      // Authentication & User Management
      'auth/login': 'auth/login',
      'auth/register': 'auth/register',
      'auth/verify': 'auth/verify',
      'auth/me': 'auth/me',
      'users/profile': 'auth/profile',

      // Events Management
      events: 'events',
      'events/create': 'events',
      'events/generate': 'admin/generate',
      'events/recurring': 'events',

      // Host Tools endpoints
      'host-tools/promotion-plan': 'host-tools',
      'host-tools/event-script': 'host-tools',
      'host-tools/equipment-list': 'host-tools',
      'host-tools/budget-plan': 'host-tools',
      'host-tools/weather-plan': 'host-tools',

      // Discovery & Recommendations
      'events/recommended': 'discovery/recommendations',
      'events/search': 'discovery/search',

      // Saved Events & User Data
      'users/saved-events': 'user-data/saved-events',
      'users/saved-count': 'user-data/saved-count',
      'events/popular-saved': 'user-data/popular-saved',
      'users/preferences': 'user-data/preferences',
      'users/interests': 'user-data/interests',

      // Host Management
      'host/events': 'host/events',
      'host/profile': 'host/profile',
      'host/stats': 'host/stats',
      'host/analytics': 'host/analytics',

      // RSVP Management
      'users/me/rsvps': 'rsvp/user',

      // Admin Features
      'admin/events': 'admin/events',
      'admin/analytics': 'analytics/admin',

      // Onboarding
      'onboarding/user/status': 'onboarding/user/status',
      'onboarding/user/complete': 'onboarding/user/complete',
      'onboarding/user/preferences': 'onboarding/user/preferences',
      'onboarding/host/complete': 'onboarding/host/complete',

      // Newsletter & Misc
      'newsletter/subscribe': 'events/newsletter',

      // Payment Processing
      orders: 'stripe/orders',
      stripe: 'stripe',

      // Background Jobs (for testing/debugging)
      'jobs/reminders/send-pending': 'reminder-scheduler?action=send-pending',
      'jobs/reminders/schedule-daily': 'reminder-scheduler?action=schedule-daily',
      'jobs/metrics/hourly': 'metrics-aggregator?action=hourly',
      'jobs/metrics/daily': 'metrics-aggregator?action=daily',
      'jobs/metrics/trending': 'metrics-aggregator?action=trending',

      // Flyer Generation
      'events/flyer': 'admin/generate-flyer',
      'events/flyer/v2': 'admin/generate-flyer-v2',
    };

    // Special handling for host-tools endpoints
    if (cleanEndpoint.startsWith('host-tools/')) {
      // All host-tools endpoints should go directly to the Supabase Edge Function
      // This ensures proper request body handling
      return `${API_BASE_URL}/${cleanEndpoint}`;
    }

    // RSVP endpoints use direct paths now
    if (cleanEndpoint.startsWith('rsvp/')) {
      return `${API_BASE_URL}/${cleanEndpoint}`;
    }

    // Special handling for flyer generation endpoints
    if (cleanEndpoint.match(/^events\/[a-f0-9-]+\/flyer\/v2/)) {
      // For now, use the events endpoint directly until flyer generation is properly set up
      // This will use the main backend API for flyer generation
      const eventId = cleanEndpoint.split('/')[1];
      // Use the non-Supabase API URL if available for flyer generation
      // Prefer local API only in development when explicitly configured
      if (!isProd && process.env.REACT_APP_API_URL) {
        const flyerApiUrl = process.env.REACT_APP_API_URL;
        return `${flyerApiUrl}/events/${eventId}/flyer/v2`;
      }
      // Otherwise, route to Supabase base to avoid localhost in prod
      return `${API_BASE_URL}/events/${eventId}/flyer/v2`;
    }

    if (cleanEndpoint.match(/^events\/[a-f0-9-]+\/flyer/)) {
      // events/{id}/flyer -> admin/generate-flyer?eventId={id}
      const eventId = cleanEndpoint.split('/')[1];
      return `${API_BASE_URL}/admin/generate-flyer?eventId=${eventId}`;
    }

    // Check if we have a mapping for this endpoint
    for (const [oldPath, newPath] of Object.entries(endpointMappings)) {
      if (cleanEndpoint.startsWith(oldPath)) {
        const mappedEndpoint = cleanEndpoint.replace(oldPath, newPath);
        return `${API_BASE_URL}/${mappedEndpoint}`;
      }
    }
  }

  return `${API_BASE_URL}/${cleanEndpoint}`;
}; // End of legacy mapping function

// Check if we're using Supabase
export const isUsingSupabase = () => useSupabase;

// Lightweight API health check (optional)
export const checkApiHealth = async () => {
  try {
    const url = buildApiUrl('health');
    const res = await fetch(url, { method: 'GET' });
    return res.ok;
  } catch (_) {
    return false;
  }
};

// Get auth headers for API calls
export const getAuthHeaders = async () => {
  // Prefer Supabase auth token when available (regardless of useSupabase flag)
  try {
    if (process.env.REACT_APP_SUPABASE_URL) {
      // Import supabase directly instead of using the hook to avoid timing issues
      const { supabase } = await import('../lib/supabaseClient');
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.warn('Error getting session for auth headers:', error);
      }
      const headers = {};
      // Always include anon apikey when available for Supabase REST/Edge
      if (process.env.REACT_APP_SUPABASE_ANON_KEY) {
        headers.apikey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      }
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }
      if (Object.keys(headers).length > 0) return headers;
    }
  } catch (error) {
    console.warn('Error getting Supabase auth headers:', error);
  }

  // Fallback to legacy token handling for local backend
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  if (process.env.REACT_APP_SUPABASE_ANON_KEY)
    headers.apikey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  return headers;
};

const apiConfig = {
  API_BASE_URL,
  BACKEND_URL,
  buildApiUrl,
  isUsingSupabase,
  getAuthHeaders,
};

export default apiConfig;
